import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { PrimeReactProvider, addLocale } from 'primereact/api';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { locale } from './locales/es';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { CatalogProvider } from './contexts/CatalogContext';

// Pages
import LandingPage from './pages/LandingPage';
import Catalog from './pages/Catalog';
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import MediosPagoPage from './pages/MediosPagoPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import CambiosDevolucionesPage from './pages/CambiosDevolucionesPage';
import EnviosPage from './pages/EnviosPage';
import FaqPage from './pages/FaqPage';
import GarantiasPage from './pages/GarantiasPage';
import ProcesoCompraPage from './pages/ProcesoCompraPage';
import NosotrosPage from './pages/NosotrosPage';
import TerminosPage from './pages/TerminosPage';
import PrivacidadPage from './pages/PrivacidadPage';
import ContactoPage from './pages/ContactoPage';
import EliminarDatosPage from './pages/EliminarDatosPage';
import LibroReclamacionesPage from './pages/LibroReclamacionesPage';
import FloatingWhatsAppButton from './components/common/FloatingWhatsAppButton';
import FloatingCartButton from './components/landing/FloatingCartButton';
import SlideoverCart from './components/landing/SlideoverCart';
import { initTracking, trackSPAView } from './services/tracking';
import CookieConsent from './components/common/CookieConsent';

// Styles
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import './styles/globals.css';
import './styles/landing.css';

// PrimeReact configuration
const primeReactConfig = {
    ripple: true,
    inputStyle: 'outlined',
    locale: 'es',
    appendTo: 'self'
};


function App() {
    const [isCartOpen, setIsCartOpen] = React.useState(false);

    useEffect(() => {
        // Configurar el idioma español para PrimeReact
        addLocale('es', locale);
        // Inicializar tracking solo en producción (evita duplicados en desarrollo)
        if (process.env.NODE_ENV === 'production') {
            try {
                const consent = typeof window !== 'undefined' ? localStorage.getItem('cookie_consent') : null;
                if (consent === 'accepted') {
                    initTracking();
                }
            } catch (e) {
                // ignore localStorage access errors
            }
        }
    }, []);

    const toggleCart = () => {
        setIsCartOpen(!isCartOpen);
    };

    // Componente interno para escuchar cambios de ruta y disparar pageviews
    const AnalyticsTracker = () => {
        const location = useLocation();
        useEffect(() => {
            if (process.env.NODE_ENV === 'production') {
                trackSPAView(window.location.href, location.pathname + location.search);
            }
        }, [location.pathname, location.search]);
        return null;
    };

    return (
        <PrimeReactProvider value={primeReactConfig}>
            <HelmetProvider>
                <AuthProvider>
                    <CartProvider>
                        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                            <CatalogProvider>
                                <AnalyticsTracker />
                                <div className="App">
                                    <Routes>
                                        <Route path="/" element={<LandingPage />} />
                                        <Route path="/catalogo" element={<Catalog />} />
                                        <Route path="/catalogo/:categoria" element={<Catalog />} />
                                        <Route path="/catalogo/:categoria/:subcategoria" element={<Catalog />} />
                                        <Route path="/producto/:id" element={<ProductPage />} />
                                        <Route path="/producto/:id/:slug" element={<ProductPage />} />
                                        <Route path="/carrito" element={<CartPage />} />
                                        <Route path="/checkout" element={<CheckoutPage />} />
                                        <Route path="/orden-confirmada/:id" element={<OrderConfirmationPage />} />
                                        <Route path="/login" element={<LoginPage />} />
                                        <Route path="/register" element={<RegisterPage />} />
                                        <Route path="/profile" element={<ProfilePage />} />
                                        <Route path="/medios-pago" element={<MediosPagoPage />} />
                                        <Route path="/nosotros" element={<NosotrosPage />} />
                                        <Route path="/cambios-devoluciones" element={<CambiosDevolucionesPage />} />
                                        <Route path="/envios" element={<EnviosPage />} />
                                        <Route path="/faq" element={<FaqPage />} />
                                        <Route path="/garantias" element={<GarantiasPage />} />
                                        <Route path="/proceso-compra" element={<ProcesoCompraPage />} />
                                        <Route path="/privacidad" element={<PrivacidadPage />} />
                                        <Route path="/eliminar-datos" element={<EliminarDatosPage />} />
                                        <Route path="/contacto" element={<ContactoPage />} />
                                        <Route path="/terminos" element={<TerminosPage />} />
                                        <Route path="/libro-reclamaciones" element={<LibroReclamacionesPage />} />
                                        {/* Rutas adicionales */}
                                    </Routes>
                                    
                                    {/* Componentes flotantes */}
                                    <FloatingWhatsAppButton />
                                    <FloatingCartButton onToggleCart={toggleCart} />
                                    <SlideoverCart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
                                    <CookieConsent />
                                </div>
                                <Toaster 
                                    position="top-right"
                                    toastOptions={{
                                        duration: 4000,
                                        style: {
                                            background: '#363636',
                                            color: '#fff',
                                        },
                                    }}
                                />
                            </CatalogProvider>
                        </Router>
                    </CartProvider>
                </AuthProvider>
            </HelmetProvider>
        </PrimeReactProvider>
    );
}

export default App;
